/**
 * useLandingDraft — central state for the Landing CMS editor.
 *
 * Responsibilities:
 *   - Load editor state from /superadmin/landing-cms/state/ on mount
 *   - Hold per-language draft payloads in local state
 *   - Expose `setField(lang, path, value)` so section editors patch one
 *     leaf at a time without each editor having to know the full schema
 *   - Track "dirty" — whether the local draft differs from what the
 *     server last told us about (so we know when to enable Save Draft)
 *   - Save / publish / discard / restore — wrap landingCmsApi
 *
 * Path semantics: a path is a dotted string like
 *   "hero.slides.0.title"   →   draft.hero.slides[0].title
 * Array indices are written as `"0"`, `"1"` etc.  We chose dotted strings
 * over arrays because every section editor wires them through JSX props
 * and dotted strings read cleaner in the source.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { landingCmsApi } from './landingCmsApi'

// ----- deep-set helper -------------------------------------------------------
// Returns a new object with `path` set to `value`, structurally sharing
// untouched branches.  Creates arrays for numeric segments and objects
// otherwise.  No external dep — keeps this file self-contained.
function deepSet(obj, path, value) {
  const segments = String(path).split('.')
  const next = Array.isArray(obj) ? [...obj] : { ...(obj || {}) }
  let cursor = next
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]
    const nextSegIsIndex = /^\d+$/.test(segments[i + 1])
    let child = cursor[seg]
    if (child == null || typeof child !== 'object') {
      child = nextSegIsIndex ? [] : {}
    } else {
      child = Array.isArray(child) ? [...child] : { ...child }
    }
    cursor[seg] = child
    cursor = child
  }
  cursor[segments[segments.length - 1]] = value
  return next
}

function deepGet(obj, path, fallback) {
  if (!obj || !path) return fallback
  const segments = String(path).split('.')
  let cursor = obj
  for (const seg of segments) {
    if (cursor == null) return fallback
    cursor = cursor[seg]
  }
  return cursor === undefined ? fallback : cursor
}

function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

// ----- the hook --------------------------------------------------------------

export function useLandingDraft() {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // Server-known published baselines + last-saved-draft baselines.
  const [publishedEn, setPublishedEn] = useState({})
  const [publishedAr, setPublishedAr] = useState({})
  const [savedDraftEn, setSavedDraftEn] = useState({})
  const [savedDraftAr, setSavedDraftAr] = useState({})

  // Local working copies the editor is mutating right now.
  const [draftEn, setDraftEn] = useState({})
  const [draftAr, setDraftAr] = useState({})

  // Settings (hero mode, language switch, video URLs).  Saved instantly on
  // change, not gated behind Publish.
  const [settings, setSettings] = useState({
    hero_mode: 'slider',
    language_switch_enabled: true,
    hero_video_url: '',
    hero_video_poster_url: '',
  })

  const [savingDraft, setSavingDraft] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)

  // ---- initial load ----
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await landingCmsApi.getState()
        if (cancelled) return
        const en = data?.content?.en || {}
        const ar = data?.content?.ar || {}
        setPublishedEn(en.published_payload || {})
        setPublishedAr(ar.published_payload || {})
        setSavedDraftEn(en.draft_payload || {})
        setSavedDraftAr(ar.draft_payload || {})
        setDraftEn(en.draft_payload || {})
        setDraftAr(ar.draft_payload || {})
        if (data?.settings) {
          setSettings({
            hero_mode: data.settings.hero_mode || 'slider',
            language_switch_enabled: data.settings.language_switch_enabled !== false,
            hero_video_url: data.settings.hero_video_url || '',
            hero_video_poster_url: data.settings.hero_video_poster_url || '',
          })
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.response?.data?.message || 'Failed to load landing CMS state')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // ---- mutators exposed to section editors ----

  /** Patch one leaf in one language's draft. */
  const setField = useCallback((lang, path, value) => {
    if (lang === 'en') setDraftEn((d) => deepSet(d, path, value))
    else if (lang === 'ar') setDraftAr((d) => deepSet(d, path, value))
  }, [])

  /** Read one leaf. Defaults to empty string for missing string fields. */
  const getField = useCallback((lang, path, fallback = '') => {
    const src = lang === 'en' ? draftEn : draftAr
    return deepGet(src, path, fallback)
  }, [draftEn, draftAr])

  /** Replace an entire sub-tree (used when adding/removing array items). */
  const setSubtree = useCallback((lang, path, value) => {
    if (!path) {
      if (lang === 'en') setDraftEn(value)
      else if (lang === 'ar') setDraftAr(value)
      return
    }
    setField(lang, path, value)
  }, [setField])

  /** Update one settings field and persist immediately. */
  const updateSettings = useCallback(async (patch) => {
    setSavingSettings(true)
    try {
      const data = await landingCmsApi.saveSettings(patch)
      setSettings((s) => ({ ...s, ...(data || patch) }))
      return { ok: true }
    } catch (err) {
      return { ok: false, error: err?.response?.data?.message || 'Failed to save settings' }
    } finally {
      setSavingSettings(false)
    }
  }, [])

  /** Save draft for one language (or both with parallel calls). */
  const saveDraft = useCallback(async () => {
    setSavingDraft(true)
    try {
      const [enRes, arRes] = await Promise.all([
        deepEqual(draftEn, savedDraftEn) ? null : landingCmsApi.saveDraft('en', draftEn),
        deepEqual(draftAr, savedDraftAr) ? null : landingCmsApi.saveDraft('ar', draftAr),
      ])
      // After successful save, our "saved" baselines move forward.
      setSavedDraftEn(draftEn)
      setSavedDraftAr(draftAr)
      return { ok: true, enSaved: !!enRes, arSaved: !!arRes }
    } catch (err) {
      return { ok: false, error: err?.response?.data?.message || 'Failed to save draft' }
    } finally {
      setSavingDraft(false)
    }
  }, [draftEn, draftAr, savedDraftEn, savedDraftAr])

  /** Publish — assumes save was already called. */
  const publish = useCallback(async (note = '') => {
    setPublishing(true)
    try {
      // Save any pending edits first to keep the API contract clean.
      if (!deepEqual(draftEn, savedDraftEn)) await landingCmsApi.saveDraft('en', draftEn)
      if (!deepEqual(draftAr, savedDraftAr)) await landingCmsApi.saveDraft('ar', draftAr)
      const res = await landingCmsApi.publish(note)
      // Drafts now equal published.
      setSavedDraftEn(draftEn); setSavedDraftAr(draftAr)
      setPublishedEn(draftEn); setPublishedAr(draftAr)
      return { ok: true, ...res }
    } catch (err) {
      return { ok: false, error: err?.response?.data?.message || 'Failed to publish' }
    } finally {
      setPublishing(false)
    }
  }, [draftEn, draftAr, savedDraftEn, savedDraftAr])

  /**
   * Replace one language's entire draft with an imported payload (parsed
   * from an uploaded JSON file).  Server-side does the same via the
   * /import/<lang>/ endpoint, but we also update local state immediately
   * so the editor reflects the change without a round-trip.
   */
  const importPayload = useCallback(async (lang, payload) => {
    if (!payload || typeof payload !== 'object') {
      return { ok: false, error: 'Imported file is empty or not an object' }
    }
    try {
      await landingCmsApi.importPayload(lang, payload)
      if (lang === 'en') {
        setDraftEn(payload); setSavedDraftEn(payload)
      } else if (lang === 'ar') {
        setDraftAr(payload); setSavedDraftAr(payload)
      }
      return { ok: true }
    } catch (err) {
      return { ok: false, error: err?.response?.data?.message || 'Import failed' }
    }
  }, [])

  /** Discard — drop local edits, reset to last published. */
  const discardDraft = useCallback(async () => {
    try {
      await landingCmsApi.discard()
      setDraftEn(publishedEn); setDraftAr(publishedAr)
      setSavedDraftEn(publishedEn); setSavedDraftAr(publishedAr)
      return { ok: true }
    } catch (err) {
      return { ok: false, error: err?.response?.data?.message || 'Failed to discard' }
    }
  }, [publishedEn, publishedAr])

  // ---- derived state ----
  const isDirty = useMemo(
    () => !deepEqual(draftEn, savedDraftEn) || !deepEqual(draftAr, savedDraftAr),
    [draftEn, draftAr, savedDraftEn, savedDraftAr]
  )
  const hasUnpublishedChanges = useMemo(
    () => !deepEqual(draftEn, publishedEn) || !deepEqual(draftAr, publishedAr),
    [draftEn, draftAr, publishedEn, publishedAr]
  )

  return {
    loading, loadError,
    draftEn, draftAr,
    settings,
    setField, getField, setSubtree,
    updateSettings,
    saveDraft, publish, discardDraft, importPayload,
    savingDraft, publishing, savingSettings,
    isDirty, hasUnpublishedChanges,
  }
}
